/**
 * SubmissionRepository — data access for FormSubmission entities.
 *
 * Follows FERP repository pattern: constructor DI of ApolloClient.
 * Handles form submission (the core intake mutation) and status lookups.
 * UI components never import this directly — use hooks instead.
 */
import { ApolloClient, gql } from '@apollo/client/core';
import type { FormSubmission } from '../types';
import { apolloClient } from '../../client';

const SUBMIT_FORM = gql`
  mutation SubmitForm($portalSlug: String!, $workflowSlug: String!, $data: JSON!) {
    submitForm(portalSlug: $portalSlug, workflowSlug: $workflowSlug, data: $data) {
      id
      status
      submittedAt
    }
  }
`;

const GET_FORM_SUBMISSION = gql`
  query GetFormSubmission($id: ID!) {
    formSubmission(id: $id) {
      id
      status
      ferpInstanceId
      submittedAt
      processedAt
      errorMessage
    }
  }
`;

export interface SubmitFormInput {
  portalSlug: string;
  workflowSlug: string;
  data: Record<string, unknown>;
}

export class SubmissionRepository {
  constructor(private readonly client: ApolloClient) {}

  async submit(input: SubmitFormInput): Promise<FormSubmission> {
    const { data } = await this.client.mutate<{ submitForm: FormSubmission }>({
      mutation: SUBMIT_FORM,
      variables: {
        portalSlug: input.portalSlug,
        workflowSlug: input.workflowSlug,
        data: input.data,
      },
    });
    return data!.submitForm;
  }

  async getById(id: string): Promise<FormSubmission | null> {
    const { data } = await this.client.query<{ formSubmission: FormSubmission | null }>({
      query: GET_FORM_SUBMISSION,
      variables: { id },
    });
    return data?.formSubmission ?? null;
  }
}

export const submissionRepository = new SubmissionRepository(apolloClient);
